/**
 * Shared Score Trend series builder (#1859).
 *
 * Operates on backend ScoreTrendEntry objects (GET /api/archers/{id}/stats →
 * score_trend): { round_id, total, date, target_face, kind, location,
 * bow_style, arrow_count, max_per_arrow, x_count }. Both Home's and Stats'
 * Score Trend panels build their chart series here, so the avg-per-arrow
 * convention can no longer drift between the two pages.
 *
 * #1728: the plotted value is the round's average arrow score
 * (total / arrow_count), never the raw round total — round totals aren't
 * comparable across round lengths (a 40-arrow session and a 900-round attempt
 * would otherwise land on the same axis). Each point's `total` field holds the
 * plotted value, so ScoreTrendChart needs no changes. Entries with no arrows
 * are dropped defensively.
 */
import { CATEGORICAL_PALETTE } from "./statsColors";

// Avg arrow score for one ScoreTrendEntry; null when the entry has no arrows.
export function avgPerArrow(entry) {
  return entry.arrow_count > 0 ? Number((entry.total / entry.arrow_count).toFixed(2)) : null;
}

/**
 * Group ScoreTrendEntry objects into ScoreTrendChart series.
 *
 * The backend already windows, orders (shot date, #1853) and stats-filters the
 * entries — they are consumed in the order given, never re-sorted client-side.
 *
 * @param {object[]} entries  Backend ScoreTrendEntry list.
 * @param {object}   opts
 * @param {string|null} opts.groupBy  Entry field to split series on (e.g.
 *   "max_per_arrow", "kind"); missing values group under "Other". null → one
 *   series keyed "all".
 * @param {(key, index) => {id, label, color}} opts.metaOf  Presentation meta
 *   for a series; `index` counts groups in first-seen order.
 * @param {number} opts.minPoints  Series with fewer points are dropped
 *   (default 2 — a single point can't draw a line).
 * @param {(a, b) => number} [opts.sortSeries]  Optional comparator for the
 *   final series order; defaults to first-seen group order.
 */
export function buildSeries(entries, { groupBy = null, metaOf, minPoints = 2, sortSeries = null } = {}) {
  const groups = new Map();
  for (const e of entries) {
    const avg = avgPerArrow(e);
    if (avg == null) continue;
    const key = groupBy == null ? "all" : e[groupBy] ?? "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ total: avg, date: e.date });
  }
  const series = [...groups.entries()]
    .map(([key, points], i) => ({ ...metaOf(key, i), points }))
    .filter((s) => s.points.length >= minPoints);
  return sortSeries ? series.sort(sortSeries) : series;
}

// ─── Score trend dimension picker ─────────────────────────────────────────────
// Moved here from Stats.jsx (#1939) so both panels share one series contract.

export const TREND_DIMS = [
  { id: "none",      label: "All" },
  { id: "kind",      label: "Kind",        field: "kind" },
  { id: "location",  label: "Location",    field: "location" },
  { id: "face",      label: "Target face", field: "target_face" },
  { id: "bow_style", label: "Bow style",   field: "bow_style" },
];

// #2074: the series palette is CATEGORICAL_PALETTE (lib/statsColors), the one
// categorical ramp. The local SERIES_COLORS that used to sit here was a second,
// different 5-colour list — same page, adjacent panels, unrelated colours.

// #1728: the Score Trend plots avg score per arrow (total / arrow_count),
// never the raw round total. This wrapper maps a TREND_DIMS dimension id onto
// buildSeries; it is the single series builder for BOTH Score Trend panels
// (Home passes "none", Stats passes its dim-picker selection — #1939).
export function buildTrendSeries(trend, dimId) {
  if (!trend.length) return [];
  if (dimId === "none") {
    return buildSeries(trend, {
      // The ungrouped series takes slot 1, the same colour a grouped chart's
      // first series gets — toggling the dim picker no longer recolours it.
      metaOf: () => ({ id: "all", label: "All", color: CATEGORICAL_PALETTE[0] }),
      // A lone round still shows as a single point on the ungrouped chart.
      minPoints: 1,
    });
  }
  const dim = TREND_DIMS.find(d => d.id === dimId);
  if (!dim) return [];
  return buildSeries(trend, {
    groupBy: dim.field,
    metaOf: (key, i) => ({
      id: key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      color: CATEGORICAL_PALETTE[i % CATEGORICAL_PALETTE.length],
    }),
  });
}
